import { useParams } from "react-router";
import { useGetCountiesByStateQuery, useGetCountyDetailsByStateAndCountyQuery } from "../../redux/countySlice";
import { Container, Col, Row, Card, Form } from "react-bootstrap";
import Sales from "./Sales";
import "./County.css";
import { Globe, Phone, ExternalLink, Mail, Map, CreditCard } from "lucide-react";
import ScheduledSales from "./ScheduledSales";
import { Link } from "react-router-dom";
import { formatPhoneNumber } from "../../utilities/formatting";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";

const SRI_CONTACT_INFO = {
  phone: "(800) 800-9588",
  email: "support@sriservices.com",
};

const CountyResourceCard = ({ href, label, Icon }) => {
  const card = (
    <Card
      className={`h-100 w-100 ${
        href ? "county-link-card" : "county-link-card-muted text-muted"
      }`}
    >
      <Card.Body className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-2">
          <Icon size={20} className={href ? "orange" : "text-muted"} />
          <span className={`fw-medium ${href ? "text-dark" : "text-muted"}`}>{label}</span>
        </div>
        <ExternalLink size={18} className={href ? "orange" : "text-muted"} aria-hidden="true" />
      </Card.Body>
    </Card>
  );

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-decoration-none"
        aria-label={`Open ${label}`}
      >
        {card}
      </a>
    );
  }

  return (
    <div role="group" aria-label={`${label} unavailable`}>
      {card}
    </div>
  );
};

const County = () => {
  const { countyName, stateCode } = useParams();
  const { data: countyList = [] } = useGetCountiesByStateQuery({ stateCode });
  const {
    data: { sheriffDetails, tsmDetails } = {},
    isLoading,
    isError,
  } = useGetCountyDetailsByStateAndCountyQuery({
    stateCode,
    countyName,
  });


  const formattedCountyName = countyName
    .split(" ")
    .map((word) => { return word.charAt(0).toUpperCase() + word.slice(1) })
    .join(" ");

  const matchedCounty = countyList.find(
    (c) => c.id.toLowerCase() === countyName.toLowerCase()
  );

  const displayName = matchedCounty?.name ?? `${formattedCountyName} County`;

  useDocumentTitle(`${displayName}, ${stateCode?.toUpperCase()}`);

  const getLocationLabel = (displayName) => {
    if (/parish/i.test(displayName)) return "Parish";
    if (/city/i.test(displayName)) return "City";
    if (/town/i.test(displayName)) return "Town";
    return "County";
  };

  const locationLabel = getLocationLabel(displayName);

  const errorDisplay = () => {
    return (
      <div className="countyError">
        <h4>Something went wrong. </h4>
        <p>
          We are unable to display information about {displayName}, {stateCode}
        </p>
        <p>Here are some things you can try in the meantime:</p>
        <ul>
          <li>Refresh the page (sometimes this helps).</li>
          <li>Come back in 30 minutes.</li>
          <li>
            Connect with our support team via{" "}
            <Link className="onErrorLink" to="/support">
              /support
            </Link>
            .
          </li>
        </ul>
      </div>
    );
  };

  const emptyDisplay = () => {
    return (
      <div className="countyError">
        <h4>No Data Available. </h4>
        <p>
          We currently do not have information about {displayName}, {stateCode}
        </p>
      </div>
    );
  };

  return (
    <Container className="p-2 px-0 px-sm-3" style={{ boxShadow: 'none' }}>
      {isLoading ? (
        <p role="status" aria-live="polite">Loading county information</p>
      ) : isError ? (
        errorDisplay()
      ) : !tsmDetails || tsmDetails.length === 0 ? (
        emptyDisplay()
      ) : (
        <div>
          {
            <div className="countyPage">
              <h1>{displayName}</h1>
              <Row>
                <Col md={6}>
                  <Card className="h-100 w-100 p-4 softCard">
                    <h2>
                      <Globe className="countyIcons icon" aria-hidden="true" /> {locationLabel} Information
                    </h2>
                    <Form.Group className="mb-3">
                      <div className="text-secondary fw-medium mb-1">{locationLabel} Website</div>

                      {tsmDetails?.WebSite ? (
                        <a
                          href={tsmDetails.WebSite}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="d-inline-flex align-items-center gap-1 text-decoration-none orange"
                        >
                          <ExternalLink size={16} aria-hidden="true" />
                          {tsmDetails.WebSite.replace(/(^\w+:|^)\/\//, "")}
                        </a>
                      ) : (
                        <span className="text-muted">No website available</span>
                      )}
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <div className="text-secondary fw-medium mb-1">General Office Number</div>

                      {tsmDetails?.GeneralOfficePhone ? (
                        <a
                          href={`tel:${tsmDetails.GeneralOfficePhone}`}
                          className="d-inline-flex align-items-center gap-1 text-decoration-none orange"
                        >
                          <Phone size={16} aria-hidden="true" />
                          {formatPhoneNumber(tsmDetails.GeneralOfficePhone)}
                        </a>
                      ) : (
                        <span className="text-muted">No phone available</span>
                      )}
                    </Form.Group>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="h-100 w-100 p-4 softCard">
                    <h2>
                      <Phone className="countyIcons icon" aria-hidden="true" /> SRI Contact Information
                    </h2>
                    <Form.Group className="mb-3">
                      <div className="text-secondary fw-medium mb-1">Phone</div>

                      <a
                        href={`tel:${SRI_CONTACT_INFO.phone}`}
                        className="d-inline-flex align-items-center gap-1 text-decoration-none orange"
                      >
                        <Phone size={16} aria-hidden="true" />
                        {formatPhoneNumber(SRI_CONTACT_INFO.phone)}
                      </a>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <div className="text-secondary fw-medium mb-1">Email</div>

                      <a
                        href={`mailto:${SRI_CONTACT_INFO.email}`}
                        className="d-inline-flex align-items-center gap-1 text-decoration-none orange"
                      >
                        <Mail size={16} aria-hidden="true" />
                        {SRI_CONTACT_INFO.email}
                      </a>
                    </Form.Group>
                  </Card>
                </Col>
              </Row>
              <Row className="g-4 mt-2">
                <Col md={6}>
                  <CountyResourceCard
                    href={tsmDetails?.GisSystemLink || null}
                    label="GIS System"
                    Icon={Map}
                  />
                </Col>

                <Col md={6}>
                  <CountyResourceCard
                    href={tsmDetails?.TaxSystemLink || null}
                    label="Tax System"
                    Icon={CreditCard}
                  />
                </Col>
              </Row>
              <Row className="g-4 mt-2">
                <Col md={12}>
                  <Card className="h-100 w-100 p-4">
                    <Sales
                      countyName={countyName}
                      stateCode={stateCode}
                      tsmDetails={tsmDetails}
                      sheriffDetails={sheriffDetails}
                    />
                  </Card>
                </Col>
              </Row>
              <Row className="g-4 mt-2">
                <Col md={12}>
                  <Card className="h-100 w-100 p-4">
                    <ScheduledSales countyName={countyName} stateCode={stateCode} />
                  </Card>
                </Col>
              </Row>
            </div>
          }
        </div>
      )}
    </Container>
  );
};

export default County;